import { Alert, Box, Modal, Typography } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/styles';
import { createNftBid } from 'APIs/HotAuctionApi';
import { useAppSelector } from 'app/hooks';
import { NormalButton, SmallButton } from 'components/atoms/Button';
import Loader from 'components/atoms/loader';
import SuperScriptStr from 'components/atoms/String/SuperScriptStr';
import InputText from 'components/molecules/InputText/InputText';
import { selectAuth } from 'features/auth/authSlice';
import NftData from 'interfaces/NftData.interface';
import moment from 'moment';
import { SetStateAction, useEffect, useState } from 'react';
import { approveNftAuction, bidNftAuction, sellNftAuction } from 'APIs/tatum';
import { v4 } from 'uuid';

const PlaceBidModal = styled(Modal)(({ theme }) => ({
  '& .body': {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    maxWidth: 450,
    padding: '3rem',
    borderRadius: '30px',
    backgroundColor: '#292739',
  },
}));

const ParaText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#ffffff',
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: '2.4rem',
  fontWeight: 'bold',
  color: '#ffffff',
}));

interface IBidModalProps {
  children: JSX.Element;
  nft: NftData;
  setNft: SetStateAction<any>;
}

const BidModal = ({ children, nft, setNft }: IBidModalProps) => {
  const [bidFormOpen, setBidFormOpen] = useState(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [currentBid, setCurrentBid] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const authState = useAppSelector(selectAuth);

  useEffect(() => {
    const findTime = setInterval(() => {
      if (nft.endsAt) {
        const startTime = moment();
        const endTime = moment(nft.endsAt);
        const duration = moment.duration(endTime.diff(startTime));
        const hours = parseInt(duration.asHours() as any);
        const minutes = parseInt((duration.asMinutes() % 60) as any);
        const seconds = parseInt((duration.asSeconds() % 60) as any);
        setTimeLeft(hours + '//' + minutes + '/-/' + seconds);
      }
    }, 1000);
    return () => clearInterval(findTime);
  }, [nft]);

  const submitBid = async () => {
    setLoading(true);
    try {
      if (
        authState.id &&
        nft.id &&
        currentBid > nft.reservedBid
        // &&
        // nft.endsAt
      ) {
        if (nft.highestBid && nft.highestBid.bidAmount > currentBid) {
          throw new Error('Bidding amount less than highest bid.');
        }

        const id = v4();
        const date = new Date(nft.endsAt ? nft.endsAt : '') > new Date();

        if (authState.tatumWalletAddress && nft.contractAddress && !date) {
          await approveNftAuction(nft.tokenId);

          await sellNftAuction(
            nft.contractAddress,
            authState.tatumWalletAddress,
            id,
            nft.tokenId,
            2022222000
          );
        }

        await bidNftAuction(id, currentBid.toString());

        const bid = await createNftBid({
          userId: authState.id,
          nftId: nft.id,
          bidAmount: currentBid,
        });

        let tommorowDate = new Date();
        tommorowDate.setDate(tommorowDate.getDate() + 1);
        setNft({ ...nft, endsAt: tommorowDate });

        if (bid.data) {
          setSuccess(true);
          setError(false);
          // await updateNft({
          //   id: nft.id,
          //   contractAddress,
          //   auctionId,
          // });
        }
      } else {
        setError(true);
        setSuccess(false);
      }
    } catch (error) {
      console.log(error);
      setError(true);
      setSuccess(false);
    }
    setLoading(false);
  };

  return (
    <div>
      <span onClick={() => setBidFormOpen(true)}>{children}</span>
      <PlaceBidModal
        open={bidFormOpen}
        // onClose={() => setBidFormOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="body" style={{ textAlign: 'center' }}>
          <CloseIcon
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              cursor: 'pointer',
              fill: '#fff',
            }}
            onClick={() => setBidFormOpen(false)}
          />

          <TitleText style={{ fontSize: '1.5rem' }}>
            Place a bid, will ya?
          </TitleText>

          <Box style={{ margin: '2.2rem 0rem' }}>
            {nft.endsAt ? (
              <>
                {' '}
                <ParaText style={{ marginBottom: '.8rem', color: '#c1c1c1' }}>
                  Auction ends in
                </ParaText>
                <SuperScriptStr
                  text={timeLeft.split('//')[0]}
                  sup={'hr'}
                  style={{
                    color: '#e5e5e5',
                    fontSize: '1.8rem',
                    marginRight: '5px',
                  }}
                />
                <SuperScriptStr
                  text={timeLeft.split('//')[1]?.split('/-/')[0]}
                  sup={'mn'}
                  style={{
                    color: '#e5e5e5',
                    fontSize: '1.8rem',
                    marginRight: '5px',
                  }}
                />
                <SuperScriptStr
                  text={timeLeft.split('//')[1]?.split('/-/')[1]}
                  sup={'sec'}
                  style={{
                    color: '#e5e5e5',
                    fontSize: '1.8rem',
                    marginRight: '5px',
                  }}
                />{' '}
              </>
            ) : (
              <ParaText style={{ marginBottom: '.8rem', color: '#c1c1c1' }}>
                Place a bid to start auction
              </ParaText>
            )}
          </Box>

          <Box style={{ margin: '2.2rem 0rem' }}>
            <ParaText style={{ marginBottom: '.8rem', color: '#c1c1c1' }}>
              Please enter your bid. It needs to be higher than{' '}
              {nft.highestBid?.bidAmount || nft.reservedBid} MATIC
            </ParaText>
            <InputText
              onChange={e => {
                setCurrentBid(parseFloat(e.target.value));
              }}
              initValue={'currentBid'}
              type={'number'}
              name="currentBid"
              placeholder="Bid Amount"
            />
            <NormalButton
              style={{
                width: '100%',
                justifyContent: 'start',
                color: '#c1c1c1',
                marginTop: '20px',
              }}
            >
              Your Balance
              <SmallButton
                style={{ marginLeft: 'auto', color: '#c1c1c1' }}
                text={' MATIC'}
              />
            </NormalButton>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(false)}
            >
              There was an error in submitting your bid.
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccess(false)}
            >
              Your bid has been submitted successfully.
            </Alert>
          )}
          <Box>
            <NormalButton
              onClick={submitBid}
              style={{ width: '100%' }}
              color="yellow"
            >
              {loading ? <Loader size="2" /> : 'Submit Bid'}
            </NormalButton>
          </Box>
        </Box>
      </PlaceBidModal>
    </div>
  );
};

export default BidModal;
